import React, { useState, useEffect, useRef } from 'react';
import '../styles/EditorProducto.css'; // Estilos compartidos (upload area, previews, mensajes)
import '../styles/CrearProducto.css';  // Estilos específicos del modal de creación

const CrearProducto = ({ isOpen, onClose, onCrear }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    categoria: '',
    imagen: '',
    stock: '',
    stock_minimo: '3',
    proveedor: '',
    costo: '',
    recargo: '',
  });

  const [archivosNuevos, setArchivosNuevos] = useState([]);
  const [previewURLs, setPreviewURLs] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [proveedores, setProveedores] = useState([]);

  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    fetch('/api/categorias')
      .then(res => res.json())
      .then(data => setCategorias(Array.isArray(data) ? data : []))
      .catch(() => setCategorias([]));
    fetch('/api/proveedores')
      .then(res => res.json())
      .then(data => setProveedores(Array.isArray(data) ? data : []))
      .catch(() => setProveedores([]));
  }, [isOpen]);

  useEffect(() => {
    return () => {
      previewURLs.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewURLs]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'categoria' && value.toLowerCase().includes('cuadro')) {
      setFormData(prev => ({
        ...prev,
        categoria: value,
        costo:        prev.costo        === '' ? '20000' : prev.costo,
        recargo:      prev.recargo      === '' ? '50'    : prev.recargo,
        stock:        prev.stock        === '' ? '1'     : prev.stock,
        stock_minimo: prev.stock_minimo === '3' || prev.stock_minimo === '' ? '1' : prev.stock_minimo,
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    setError('');
  };

  const handleImagenesChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    previewURLs.forEach(url => URL.revokeObjectURL(url));
    const nuevasURLs = files.map(f => URL.createObjectURL(f));
    setArchivosNuevos(files);
    setPreviewURLs(nuevasURLs);
    setError('');
  };

  const eliminarArchivo = (index) => {
    URL.revokeObjectURL(previewURLs[index]);
    setArchivosNuevos(prev => prev.filter((_, i) => i !== index));
    setPreviewURLs(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    previewURLs.forEach(url => URL.revokeObjectURL(url));
    setFormData({ nombre: '', descripcion: '', categoria: '', imagen: '', stock: '', stock_minimo: '3', proveedor: '', costo: '', recargo: '' });
    setArchivosNuevos([]);
    setPreviewURLs([]);
    setError('');
    setSuccess('');
    setUploadProgress('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setUploadProgress('');

    try {
      // --- Validaciones ---
      if (!formData.nombre.trim()) {
        throw new Error('El nombre del producto es requerido');
      }
      const tieneImagenURL = formData.imagen.trim() !== '';
      const tieneArchivos = archivosNuevos.length > 0;

      if (!tieneImagenURL && !tieneArchivos) {
        throw new Error('El producto debe tener al menos una imagen');
      }

      let urlsImagenes = [];

      // --- Paso 1: Subir imágenes a Cloudinary ---
      if (tieneArchivos) {
        setUploadProgress(`Subiendo ${archivosNuevos.length} imagen(es) a Cloudinary...`);

        const formDataUpload = new FormData();
        archivosNuevos.forEach(archivo => {
          formDataUpload.append('imagenes', archivo);
        });

        const uploadRes = await fetch('/api/productos/upload', {
          method: 'POST',
          body: formDataUpload,
        });

        if (!uploadRes.ok) {
          const errData = await uploadRes.json();
          throw new Error(errData.error || 'Error al subir imágenes a Cloudinary');
        }

        const uploadData = await uploadRes.json();
        urlsImagenes = uploadData.urls || [];
        setUploadProgress('');
      }

      // Si solo hay URL manual, usarla como imagen
      if (tieneImagenURL && urlsImagenes.length === 0) {
        urlsImagenes = [formData.imagen.trim()];
      }

      // --- Paso 2: Crear el producto en la base de datos ---
      const costo   = formData.costo   !== '' ? parseFloat(formData.costo)   : null;
      const recargo = formData.recargo !== '' ? parseFloat(formData.recargo) : null;
      const precio  = (costo !== null && recargo !== null)
        ? Math.round(costo * (1 + recargo / 100))
        : 0;

      const dataToSend = {
        nombre:       formData.nombre.trim(),
        descripcion:  formData.descripcion.trim(),
        precio,
        categoria:    formData.categoria.trim(),
        imagenes:     urlsImagenes,
        imagen:       urlsImagenes[0] || formData.imagen.trim(),
        stock:        formData.stock !== '' ? parseInt(formData.stock) : 0,
        stock_minimo: formData.stock_minimo !== '' ? parseInt(formData.stock_minimo) : 3,
        proveedor:    formData.proveedor.trim(),
        ...(costo   !== null && { costo }),
        ...(recargo !== null && { recargo }),
      };

      const response = await fetch('/api/productos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear el producto');
      }

      const result = await response.json();
      setSuccess(`¡Producto "${result.producto.nombre}" creado exitosamente!`);

      setTimeout(() => {
        onCrear(result.producto);
        resetForm();
      }, 1500);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setUploadProgress('');
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <div className="crear-modal-overlay" onClick={handleCancel}>
      <div className="crear-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="crear-header">
          <h2>➕ Nuevo Producto</h2>
          <button
            className="crear-close-btn"
            onClick={handleCancel}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="crear-form">

          {/* Nombre */}
          <div className="form-group">
            <label htmlFor="c-nombre">Nombre del Producto *</label>
            <input
              type="text"
              id="c-nombre"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              placeholder="Ej: Mesa de Centro Industrial"
              disabled={loading}
              required
            />
          </div>

          {/* Descripción */}
          <div className="form-group">
            <label htmlFor="c-descripcion">Descripción</label>
            <textarea
              id="c-descripcion"
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              placeholder="Describe el producto..."
              rows="3"
              disabled={loading}
            />
          </div>

          {/* Categoría */}
          <div className="form-group">
            <label htmlFor="c-categoria">Categoría</label>
            <select
              id="c-categoria"
              name="categoria"
              value={formData.categoria}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">Sin categoría</option>
              {categorias
                .filter(c => !c.parent_id)
                .sort((a, b) => a.nombre.localeCompare(b.nombre))
                .map(padre => {
                  const subs = categorias
                    .filter(c => c.parent_id === padre._id)
                    .sort((a, b) => a.nombre.localeCompare(b.nombre));
                  return (
                    <optgroup key={padre._id} label={padre.nombre}>
                      <option value={padre.nombre}>{padre.nombre}</option>
                      {subs.map(sub => (
                        <option key={sub._id} value={sub.nombre}>  ↳ {sub.nombre}</option>
                      ))}
                    </optgroup>
                  );
                })}
            </select>
          </div>

          {/* === IMÁGENES === */}
          <div className="form-group">
            <label>Imágenes *</label>

            {/* Área de subida */}
            <div
              className={`file-upload-area ${loading ? 'disabled' : ''}`}
              onClick={() => !loading && fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImagenesChange}
                disabled={loading}
                style={{ display: 'none' }}
              />
              <span className="file-upload-icon">
                {archivosNuevos.length > 0 ? '✅' : '📷'}
              </span>
              <span className="file-upload-text">
                {archivosNuevos.length > 0
                  ? `${archivosNuevos.length} archivo(s) seleccionado(s)`
                  : 'Clic para seleccionar imágenes'}
              </span>
              <span className="file-upload-hint">
                JPG · PNG · WEBP — múltiples archivos
              </span>
            </div>

            {/* Preview */}
            {previewURLs.length > 0 && (
              <div className="imagenes-preview-section">
                <span className="imagenes-preview-label">
                  🆕 Imágenes a subir ({previewURLs.length}):
                </span>
                <div className="imagenes-preview-grid">
                  {previewURLs.map((url, idx) => (
                    <div key={idx} className="imagen-preview-item imagen-nueva">
                      <img src={url} alt={`Img ${idx + 1}`} />
                      <button
                        type="button"
                        className="imagen-remove-btn"
                        onClick={() => eliminarArchivo(idx)}
                        disabled={loading}
                        title="Quitar"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* URL manual */}
            <div className="imagen-url-legacy">
              <label htmlFor="c-imagen" className="imagen-url-legacy-label">
                ↳ O ingresá una URL directa (opcional):
              </label>
              <input
                type="url"
                id="c-imagen"
                name="imagen"
                value={formData.imagen}
                onChange={handleChange}
                placeholder="https://ejemplo.com/foto.jpg"
                disabled={loading}
              />
            </div>
          </div>

          {/* === STOCK Y COSTO === */}
          <div className="crear-seccion-titulo">Stock y costos</div>

          <div className="crear-fila-doble">
            <div className="form-group">
              <label htmlFor="c-stock">Stock inicial</label>
              <input
                type="number"
                id="c-stock"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                placeholder="0"
                min="0"
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="c-stock-minimo">Stock mínimo (alerta)</label>
              <input
                type="number"
                id="c-stock-minimo"
                name="stock_minimo"
                value={formData.stock_minimo}
                onChange={handleChange}
                placeholder="3"
                min="0"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="c-proveedor">Proveedor</label>
            <select
              id="c-proveedor"
              name="proveedor"
              value={formData.proveedor}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">Sin proveedor</option>
              {proveedores.map(p => (
                <option key={p._id} value={p.nombre}>{p.nombre}</option>
              ))}
            </select>
          </div>

          <div className="crear-fila-doble">
            <div className="form-group">
              <label htmlFor="c-costo">Costo ($)</label>
              <input
                type="number"
                id="c-costo"
                name="costo"
                value={formData.costo}
                onChange={handleChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="c-recargo">Recargo (%)</label>
              <input
                type="number"
                id="c-recargo"
                name="recargo"
                value={formData.recargo}
                onChange={handleChange}
                placeholder="30"
                min="0"
                step="0.1"
                disabled={loading}
              />
            </div>
          </div>

          {/* Progreso */}
          {uploadProgress && (
            <div className="form-message form-upload">
              <span className="spinner spinner-dark"></span>
              <span>{uploadProgress}</span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="form-message form-error">
              <span>⚠️ {error}</span>
            </div>
          )}

          {/* Éxito */}
          {success && (
            <div className="form-message form-success">
              <span>✓ {success}</span>
            </div>
          )}

          {/* Botones */}
          <div className="crear-buttons">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-crear"
              disabled={loading}
            >
              {loading ? (
                <><span className="spinner"></span> {uploadProgress ? 'Subiendo...' : 'Creando...'}</>
              ) : (
                '➕ Crear Producto'
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default CrearProducto;
